import "./TeacherAccScreen.css";

import {
    useContext,
    useMemo,
    useEffect,
    useCallback,
    useState,
    forwardRef,
    useImperativeHandle,
    useRef,
} from "react";

import { AppThemeContext } from "../../contexts/colors";
import { localeContext } from "../../contexts/localeManagement";
import { AuthContext } from "../../contexts/accountManagement";
import Card from "../../Components/Utilities/Card";
import PuffLoader from "react-spinners/PuffLoader";
import BarLoader from "react-spinners/BarLoader";
import useWindowDimensions from "../../utils/windowDimensions";
import TouchableOpacity from "../../Components/Custom/TouchableOpacity";
import { Slider, TextField } from "@mui/material";
import UtilityOverlay from "../../Components/Utilities/UtilityOverlay";

const permTable = ["Teacher", "Supervisor", "Principal", "Governorate Agent"]

const CreateAccountForm = forwardRef(function CreateAccountForm({ visible=false, setVisible=() => {}, onSubmit=() => {}, level=1 }, ref) {
	const {
		dark,
		colors
	} = useContext(AppThemeContext);
	const [requestData, setRequestData] = useState({});
	const [editing, setEditing] = useState(false);

	const [ username, setUsername ] = useState(requestData["name"] || "");
	const [ email, setEmail ] = useState(requestData["email"] || "");
	const [ permLvl, setPermLvl ] = useState(requestData["level"] || 1);

	const handleRequestDataChange = (newData) => {
		//* console.log("changing item");
		setRequestData(newData);
		setUsername(newData["name"] || "");
		setEmail(newData["email"] || "");
		setPermLvl(newData["level"] || 1);
	}

	useImperativeHandle(ref, () => ({
		getRequestData: () => requestData,
		setRequestData: (value) => handleRequestDataChange(value),
		getEditing: () => editing,
		setEditing: (value) => setEditing(value)
	}))

	useEffect(() => {
		const changeTextfieldStyles = async () => {
			await new Promise(resolve => setTimeout(resolve, 0)); // Allow layout to complete

			const normalColor = dark ? colors().lightButton : colors().listItem;
			const focusedColor = colors().success;

			const insertRule = (selector, styles) => {
				const style = document.styleSheets[0];
				const rule = `${selector} { ${styles} }`;
				style.insertRule(rule, style.cssRules.length);
			};

			insertRule('.create-teacher-account div:before', `border-bottom: 2px solid ${normalColor} !important;`);
			insertRule('.create-teacher-account div:after', `border-bottom: 2px solid ${focusedColor} !important;`);

			insertRule('.create-teacher-account div.Mui-error:before', `border-bottom: 2px solid ${colors().danger} !important;`);
			insertRule('.create-teacher-account div.Mui-error:after', `border-bottom: 2px solid ${colors().danger} !important;`);

			insertRule('.create-teacher-account div', `background-color: ${colors().gray} !important;`);
			insertRule('.create-teacher-account p', `font-weight: bold; font-family: monospace; letter-spacing: unset; font-size: 15px; color: ${colors().danger} !important;`);

			insertRule('.create-teacher-account label', `color: ${normalColor} !important; font-weight: bold`);
			insertRule('.create-teacher-account label.Mui-focused', `color: ${focusedColor} !important;`);
			insertRule('.create-teacher-account label.Mui-error', `color: ${colors().danger} !important;`);

			insertRule('#user-name, #email', `color: white !important; font-weight: bold`);

			insertRule(".MuiSlider-root.MuiSlider-colorSuccess", `color: ${colors().success};`)
			insertRule(".MuiSlider-thumb.MuiSlider-thumbColorSuccess:hover", `box-shadow: 0px 0px 0px 8px ${colors().success + "29"};`)
			insertRule(".MuiSlider-thumb.MuiSlider-thumbColorSuccess.Mui-active", `box-shadow: 0px 0px 0px 14px ${colors().success + "29"};`)
			insertRule('.MuiSlider-markLabel', `font-weight: bold !important; text-shadow: 0px 0px 1px #fff;`)

			insertRule('.MuiSlider-markLabel[data-index="0"]', `color: ${colors().secondary} !important;`)
			insertRule('.MuiSlider-markLabel[data-index="1"]', `color: ${colors().success} !important;`)
			insertRule('.MuiSlider-markLabel[data-index="2"]', `color: ${colors().warning} !important;`)
			insertRule('.MuiSlider-markLabel[data-index="3"]', `color: ${colors().danger} !important; text-align: center`)
		}

		changeTextfieldStyles();
	}, [dark]);

	const handleSubmit = () => {
		// eslint-disable-next-line no-useless-escape
		if (username?.length < 2 || !email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)) {
			return;
		}

		const newRequestData = requestData;
		newRequestData["name"] = username;
		newRequestData["email"] = email;
		newRequestData["level"] = permLvl - 1;

		setRequestData(newRequestData);

		onSubmit(newRequestData);
	}

	const handlePermChange = (e, value) => {
		//* console.log(e.target.value, value >= level, level - 1)
		if (value >= level && level !== 4) setPermLvl(level - 1);
		else setPermLvl(e.target.value);
	}

	const marks = [
		{
			value: 1,
			label: 'Teacher',
		},
		{
			value: 2,
			label: 'Supervisor',
		},
		{
			value: 3,
			label: 'Principal',
		},
		{
			value: 4,
			label: <>{`Governorate`}<br />{`Agent`}</>,
		},
	];

	return (
		<UtilityOverlay
			modalVisible={visible}
			setModalVisible={setVisible}
			modalTitle="Create Account"
			CTA="Continue"
			CTAResult={handleSubmit}
			// eslint-disable-next-line no-useless-escape
			CTADisabled={username.length < 4 || !email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)}
			useChildren
			contentHeight={420}
		>
			{/* <p>{(englishName.trim().split(" ").length < 4 && arabicName.trim().split(" ").length < 4
				? "Values"
				: englishName.trim().split(" ").length < 4
					? "English Name"
					: "Arabic Name") + " must be of at least 4 names!"}</p> */}
			<TextField
				id="user-name"
				className="create-teacher-account"
				error={username.length < 4}
				helperText={username.length < 4 ? "Username must be of at least 4 characters!" : ""}
				value={username}
				onChange={e => setUsername(e.target.value)}
				label="Username"
				variant="filled"
				style={{ marginBottom: 10 }}
			/>
			<TextField
				id="email"
				className="create-teacher-account"
				// eslint-disable-next-line no-useless-escape
				error={!email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)}
				// eslint-disable-next-line no-useless-escape
				helperText={!email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/) ? "Invalid Email Address" : ""}
				value={email}
				onChange={e => setEmail(e.target.value)}
				label="Email"
				variant="filled"
				style={{ marginBottom: 20 }}
			/>
			<Slider
				aria-label="Permission Level"
				style={{ width: "90%", marginLeft: "auto", marginRight: "auto" }}
				color="success"
				defaultValue={1}
				step={1}
				marks={marks}
				min={1}
				max={4}
				disabled={level === 1}
				value={permLvl}
				onChange={handlePermChange}

			/>
		</UtilityOverlay>
	)
})

export default function TeacherAccScreen() {
	const { colors, dark } = useContext(AppThemeContext);
	const { locale, appText } = useContext(localeContext)
	const { uId, userData, requestAssignedAccount } = useContext(AuthContext);

	const { width, height } = useWindowDimensions();

	//^ Form States
	const [ creationFormVis, setCreationFormVis ] = useState(false);
	const formRef = useRef(null);

	//^ UtilityOverlay States
	const [overlayVisible, setOverlayVisible] = useState(false);
	const [overlayData, setOverlayData] = useState({
		title: "",
		message: "",
		callToAction: "",
		success: false
	});

	const accStyles = useMemo(() => ({
		container: {
			display: "flex",
			flex: 1,
			flexDirection: "column",
			backgroundColor: colors().screen,
			justifyContent: "center",
			alignContent: "center",
			height: "100vh"
		},
		accCardContainer: {
			height: "80%",
			overflowY: 'auto',
			// overflowX: "hidden",
			display: "flex",
			flexDirection: "column",
			paddingTop: 122 + 15,
			paddingBottom: 50
		},
		row: {
			display: "flex",
			flexDirection: "row",
			flexWrap: "wrap",
			width: "100%",
		},
		column: {
			display: "flex",
			flexDirection: "column",
			flexBasis: "100%",
			flex: 1,
		}
	}), [dark])

	const [cardComponents, setCardComponents] = useState([]);
	const [loadedCards, setLoadedCards] = useState(false);
	const [submittingRequest, setSubmittingRequest] = useState(false);

	const getCards = useCallback(async () => {
		let res = {};

		const keyHeader = {
			apikey: "mawjoodStorage-Mr8Y9rInYA9yHr0lFYGT",
		};
		await fetch(`${process.env.REACT_APP_API_URL}/accounts/getAll`, {
			headers: keyHeader
		})
			.then(r => r.json())
			.then(dat => res = dat)

		// console.log(res)
		let finalComponent = [];

		for (let i = 0; i < res.length; i++) {
			finalComponent.push(
				<div style={accStyles.column}>
					<Card
						additionalStyles={{ marginLeft: "auto", marginRight: "auto" }}
						title={res[i]["name"] + ((res[i]["uId"].toString() === uId) ? " (You)" : "")}
						content={`Permission Level: ${permTable[res[i]["level"]]}`}
						footer={`User ID: ${res[i]["uId"]}`}
					/>
				</div>
			)
		}

		setCardComponents(finalComponent);

		setLoadedCards(true);
	}, [uId])

	useEffect(() => {
		getCards()
	}, [uId])

	const divideIntoRows = useCallback(() => {
		const screenWidth = width;
		let columnsPerRow;

		if (screenWidth < 750) {
			columnsPerRow = 1;
		} else if (screenWidth < 1080) {
			columnsPerRow = 2;
		} else {
			columnsPerRow = 3; // Assuming 3 columns for larger screens
		}

		// console.log(columnsPerRow)
		const rows = [];
		for (let i = 0; i < cardComponents.length; i += columnsPerRow) {
			rows.push(cardComponents.slice(i, i + columnsPerRow));
		}

		return rows.map((row, rowIndex) => (
			<div key={rowIndex} style={{ ...accStyles.row, marginBottom: rows.length > rowIndex + 1 ? 25 : 0 }}>
				{row.map((CardComp) => (
					CardComp
				))}
			</div>
		))
	}, [cardComponents, width])

	const rows = divideIntoRows(cardComponents)

	const onCreationSubmit = async (requestData) => {
		setSubmittingRequest(true)
		if (!requestData["name"] || !requestData["email"] || !requestData["level"]) return;
		//TODO: Editing

		await requestAssignedAccount(requestData).then(
			/**
			 * @param {{success: boolean, data?: object, callToAction: string, message: string}} res
			 */
			res => {
				formRef.current.setRequestData({});
				setSubmittingRequest(false);
				if (!res.success) {
					setOverlayData({
						title: "Error",
						message: appText[res.message] || res.message,
						callToAction: "Cancel",
						success: false
					});
					setOverlayVisible(true);
					return;
				}
				setOverlayData({
					title: /*appText[*/"Success!"/*]*/,
					message: appText[res.message] || res.message,
					callToAction: "Okay",
					success: true
				})
				setOverlayVisible(true);
			}
		).catch(() => setSubmittingRequest(false));
	}

	return (
		<>
			<div style={accStyles.container}>
				{/* <Card /> */}
				<div className="acc-card-container" style={accStyles.accCardContainer}>
					<h1 style={{ color: userData["level"] > 1 ? colors().success : colors().danger, textAlign: "center", marginBottom: 30 }}>
						<BarLoader loading={submittingRequest} width={"100%"} color={colors().mawjoodOther} />
						You are a {permTable[userData["level"]]}
					</h1>
					{rows}
				</div>
				{cardComponents.length === 0
					? <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignContent: "center", alignSelf: "center" }}>
						<div style={{ marginLeft: "auto", marginRight: "auto" }}>
							<PuffLoader size={100} color={colors().mawjoodOther} />
						</div>
						<h1
							style={{
								fontSize: 25,
								color: colors().mawjoodOther,
								textAlign: "center",
								marginTop: 10,
								fontFamily: "monospace",
								fontWeight: "bold"
							}}
						>
							<br />
							Getting Information...
						</h1>
					</div>
					: null}
			</div>
			<TouchableOpacity
				style={{
					display: "flex",
					flexDirection: "column",
					position: "absolute",
					bottom: 62.5,
					height: 50,
					width: 300,
					backgroundColor: dark ? colors().mawjoodOther : colors().mawjoodMain,
					padding: 15,
					borderRadius: 70 / 4,
					justifyContent: "center",
					alignItems: "center",
					alignSelf: "center"
				}}
				activeOpacity={0.7}
				disabled={!(userData["level"] > 1) || !uId || !loadedCards}
				onPress={() => setCreationFormVis(true)}
			>
				<h1 style={{ fontSize: 20, fontFamily: "Ubuntu", fontWeight: "bold", color: dark ? colors().mawjoodMain : colors().mawjoodOther, }}>Create New Account</h1>
			</TouchableOpacity>
			<CreateAccountForm onSubmit={onCreationSubmit} ref={formRef} visible={creationFormVis} setVisible={setCreationFormVis} level={userData["level"] + 1} />
			<UtilityOverlay
				modalTitle={overlayData.title}
				modalVisible={overlayVisible}
				setModalVisible={setOverlayVisible}
				colorPrimary={overlayData.success ? "" : colors().danger}
				colorSecondary={overlayData.success ? "" : colors().danger + "A2"}
				CTA={overlayData.callToAction}
				modalContent={overlayData.message}
				// CTAResult={() => navigation.navigate("Create New Class", { className: `${year}-${classLetter}` })}
				translateContent={false}
			/>
		</>
	);
}